import React from 'react';
import { Box, Typography } from '@mui/material';
import { brand } from '../../theme';
import LibraryPoster from './LibraryPoster';

/** Hidden from sight but not from keyboards and screen readers. */
const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

interface VideoChoiceProps {
  /** The radio group's name: one choice per group. */
  name: string;
  value: string;
  checked: boolean;
  onSelect: () => void;
  title: string;
  meta: string;
  posterSrc?: string | null;
}

/**
 * One video in a pick-one grid: a real radio button (arrow keys move between
 * choices, Space selects) dressed as a poster card. Selection shows only as a
 * 2px copper border.
 */
const VideoChoice: React.FC<VideoChoiceProps> = ({ name, value, checked, onSelect, title, meta, posterSrc }) => (
  <Box component="label" sx={{ display: 'block', position: 'relative', minWidth: 0, cursor: 'pointer' }}>
    <input type="radio" name={name} value={value} checked={checked} onChange={onSelect} style={visuallyHidden} />
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        border: `1px solid ${checked ? brand.copper : brand.border}`,
        transition: 'border-color 0.15s ease',
        // The selected ring's second pixel, drawn over the poster so the
        // border reads as 2px all round without shifting the grid.
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          borderRadius: '7px',
          border: `1px solid ${checked ? brand.copper : 'transparent'}`,
          pointerEvents: 'none',
        },
        '&:hover': { borderColor: checked ? brand.copper : brand.borderStrong },
        'input:focus-visible + &': { outline: `2px solid ${brand.copper}`, outlineOffset: 2 },
      }}
    >
      <LibraryPoster src={posterSrc} />
      <Box sx={{ px: 1.25, py: 1 }}>
        <Typography variant="body2" noWrap title={title} sx={{ fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
          {meta || '—'}
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default VideoChoice;
